import { Alert, Anchor, Badge, Button, Checkbox, Group, Paper, Stack, Text } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { EmptyNote } from '../../components/RecordScreen';
import { useBaseCurrency } from '../../hooks/useSeedLocation';
import { enumLabel } from '../../lib/enums';
import { notifyDone, notifyError } from '../../lib/notify';
import {
  bySurface,
  capabilityFragment,
  capabilityPhrase,
  capabilitySentence,
  memberCapabilitiesPath,
  memberRolesPath,
  onlyStaffSurfaces,
  ROLES_PATH,
  type StaffCapabilities,
  type StaffCapability,
  surfaceRank,
  useScopeNames,
} from './permissions';

interface Role {
  id: string;
  name: string;
  description?: string | null;
}

/**
 * Reading order for the sentence at the top.
 *
 * A cap or an approval is the whole difference between a server and a shift lead, so those are the
 * lines a manager is actually checking when they open somebody's page — "can she really wave
 * through a comp, and up to how much?". Everything else follows in the usual order of places. This
 * only decides what gets said first; nothing here works out what anyone is allowed to do.
 */
function notableFirst(a: StaffCapability, b: StaffCapability): number {
  const weight = (c: StaffCapability) => (c.mode === 'approve' || c.limitMinor !== null ? 0 : 1);
  return weight(a) - weight(b) || surfaceRank(a.surface) - surfaceRank(b.surface);
}

/**
 * The jobs one person holds, and — in a sentence — what those jobs actually let them do.
 *
 * Both halves are needed together and neither used to exist here. Roles could be shown but never
 * set, so in practice nobody's were ever set; and even once set, a role name told the owner nothing
 * about what the person could do at the register, because the answer lives in a permission matrix he
 * could not see from this page.
 *
 * The "what they can do" half is read from the resolved capabilities endpoint rather than worked
 * out in the browser from the roles above it. Somebody holding two roles gets the union of both,
 * with the most permissive cap winning, and that merge has to match the register exactly — a second
 * implementation of it here is a second answer to the same question, and the one on screen is the
 * one the owner would trust.
 */
export function RolesPanel({ staffId, staffName }: { staffId: string; staffName: string }) {
  const queryClient = useQueryClient();
  const currency = useBaseCurrency();
  const scopeNames = useScopeNames();
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);

  /**
   * One request answers both "which roles" and "what that adds up to", so the two can never
   * disagree on screen — which they could when the roles came from one endpoint and the meaning
   * of those roles was guessed at from another.
   */
  const resolved = useQuery({
    queryKey: listKey(memberCapabilitiesPath(staffId)),
    queryFn: () => rawApi<StaffCapabilities>('GET', memberCapabilitiesPath(staffId)),
    enabled: Boolean(staffId),
    retry: false,
  });

  const catalog = useQuery({
    queryKey: listKey(ROLES_PATH),
    queryFn: () => rawApi<Role[]>('GET', ROLES_PATH),
  });

  const heldRoles = resolved.data?.roles ?? [];
  const allRoles = catalog.data ?? [];
  const capabilities = onlyStaffSurfaces(resolved.data?.capabilities ?? []);

  const save = useMutation({
    mutationFn: () => rawApi('PUT', memberRolesPath(staffId), { roleIds: selected }),
    onSuccess: () => {
      // The capabilities are what this panel reads, so they are what has to be refetched; the
      // roles list is invalidated too because other screens still read it directly.
      void queryClient.invalidateQueries({ queryKey: listKey(memberCapabilitiesPath(staffId)) });
      void queryClient.invalidateQueries({ queryKey: listKey(memberRolesPath(staffId)) });
      setEditing(false);
      notifyDone(
        'Roles updated',
        selected.length === 0
          ? `${staffName} no longer holds any role, so a register will not let them do anything.`
          : `${staffName} now holds ${selected.length === 1 ? '1 role' : `${selected.length} roles`}.`,
      );
    },
    onError: (err) => notifyError(err, "change this person's roles"),
  });

  const openEditor = () => {
    setSelected(heldRoles.map((r) => r.roleId));
    setEditing(true);
  };

  if (resolved.isLoading) return <EmptyNote>Loading…</EmptyNote>;
  if (resolved.isError) {
    return <EmptyNote>Roles could not be loaded. Refresh the page to try again.</EmptyNote>;
  }

  /**
   * Edited in place rather than in a dialog. Everything else about this person is changed on the
   * page in front of you, and a role that opens a window instead is the seam the old separate
   * edit screen left behind.
   */
  if (editing) {
    return (
      <Stack gap="md">
        {catalog.isLoading ? (
          // Still arriving is not the same as "there are none". This panel opens the instant the
          // button is pressed, usually before the list of roles has come back, and without this
          // branch the first thing a manager saw was an instruction to go and create the eight
          // roles that were already there.
          <EmptyNote>Loading…</EmptyNote>
        ) : catalog.isError ? (
          // Distinct from "none exist": telling someone to go and create roles they already have
          // is worse than saying nothing loaded.
          <EmptyNote>The list of roles could not be loaded. Try again in a moment.</EmptyNote>
        ) : allRoles.length === 0 ? (
          <EmptyNote>No roles have been set up yet. Add one on the Roles tab of the Staff screen.</EmptyNote>
        ) : (
          <>
            <Text size="sm" c="dimmed">
              Tick every job {staffName} does here. Unticking one takes away what it allowed.
            </Text>
            <Checkbox.Group value={selected} onChange={setSelected}>
              <Stack gap="sm">
                {allRoles.map((role) => (
                  <Checkbox
                    key={role.id}
                    value={role.id}
                    label={role.name}
                    description={role.description ?? undefined}
                    disabled={save.isPending}
                  />
                ))}
              </Stack>
            </Checkbox.Group>
          </>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setEditing(false)} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => save.mutate()}
            loading={save.isPending}
            disabled={allRoles.length === 0 || catalog.isLoading}
          >
            Save roles
          </Button>
        </Group>
      </Stack>
    );
  }

  const phrases = [...capabilities]
    .sort(notableFirst)
    .map((c) => capabilityFragment(c, scopeNames(c.scopeKind, c.scopeIds), currency));

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <div style={{ minWidth: 0 }}>
          {heldRoles.length === 0 ? (
            <EmptyNote>No roles yet.</EmptyNote>
          ) : (
            <Group gap="xs">
              {heldRoles.map((r) => (
                // A role name is a link because the next question is always "and what does that
                // one allow?" — which is a page, not a tooltip.
                <Anchor key={r.roleId} component={Link} to={`/staff/roles/${r.roleId}`}>
                  <Badge variant="light" size="lg" style={{ cursor: 'pointer' }}>
                    {r.name}
                  </Badge>
                </Anchor>
              ))}
            </Group>
          )}
        </div>
        <Button size="sm" variant="light" onClick={openEditor}>
          {heldRoles.length === 0 ? 'Add roles' : 'Change roles'}
        </Button>
      </Group>

      {/*
        Employment status rides on the same answer as the capabilities, and is read here rather
        than dropped: the resolver hands back everything a leaver's roles still allow, so without
        it the sentence describes somebody who stopped working here in March in the present tense.
      */}
      <Text size="sm">
        {capabilitySentence(staffName, phrases, heldRoles.length > 0, resolved.data?.status !== 'terminated')}
      </Text>

      {capabilities.length > 0 && (
        <Paper withBorder radius="sm" p="md" bg="var(--mantine-color-body)">
          <Stack gap="sm">
            {bySurface(capabilities).map(([surface, rows]) => (
              <div key={surface}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700} style={{ letterSpacing: '0.06em' }}>
                  {enumLabel('permissionSurface', surface)}
                </Text>
                <Stack gap={2} mt={4}>
                  {rows.map((c) => (
                    <Text key={`${c.action}-${c.mode}`} size="sm">
                      {capabilityPhrase(c, scopeNames(c.scopeKind, c.scopeIds), currency)}
                    </Text>
                  ))}
                </Stack>
              </div>
            ))}
          </Stack>
        </Paper>
      )}

      {/*
        Only worth shouting about for somebody who still works here — a leaver whose roles allow
        nothing is not a problem waiting to happen at a register, and the sentence above already says
        the useful thing about them. Written in the future tense because that is what is true: a
        ticked box is what a register will allow when the PIN is typed, not a door standing open now.
      */}
      {heldRoles.length > 0 && capabilities.length === 0 && resolved.data?.status !== 'terminated' && (
        <Alert color="yellow" variant="light" title="These roles allow nothing yet">
          {staffName} will get past the PIN pad on a register and then find nothing they can do. Open a role
          above to say what it covers.
        </Alert>
      )}
    </Stack>
  );
}
